/** Itemized Rehab Calculator (v1 REHAB2 + rehabCalc, leads-list.js /
 * permits-roi.js / buyers-agent.js). The 24-line scope-of-work sheet: check the
 * fixes a property needs, tune rate/qty/size, and it totals the rehab. The total
 * mirrors into the canonical `rehab` edit — which v2's Deal Engine, offer sheet,
 * Net-ROI and Est-Money chains already read (estMoney.ts, scripts.ts,
 * dealEngine.ts), so a saved sheet reprices every downstream number, exactly
 * like v1's leadRehabValue.
 *
 * Pure engine here; the table UI is RehabCalculator.tsx. Persisted through the
 * lead_fields door as an object edit (rep:['rehabCalc'] = whole-object replace,
 * same pattern as compsTool), shared byte-for-byte with v1's edits.rehabCalc. */
import type { RecOp } from "../../lib/outbox";
import { moneyExact } from "../../lib/money";
import { natKey } from "../../lib/natkey";
import type { Lead } from "../../types";

export type RehabKind = "sqft" | "size" | "flat" | "qty";
export type RehabSize = "Small" | "Medium" | "Large";

export interface RehabItem {
  n: string; // fix name
  kind: RehabKind;
  ref: string; // price-reference hint shown in the table
  on: boolean; // default checked (v1: every row defaults off)
  rate?: number; // sqft: $/sqft
  base?: "house" | "building"; // sqft: which sqft to multiply
  amt?: number; // flat/qty: $ (per unit for qty)
  sizes?: { Small: number; Medium: number; Large: number }; // size
}

/** Per-row saved overrides (v1 rehabCalc.rows[k]). All optional — absent means
 * "use the catalog default". */
export interface RehabRow {
  on?: boolean;
  rate?: number;
  qty?: number;
  amt?: number;
  size?: RehabSize;
}

export interface RehabCalcState {
  house: number | null; // living/heated sqft
  bldg: number | null; // gross building sqft
  rows: Record<number, RehabRow>; // keyed by REHAB_ITEMS index
}

/** v1 REHAB2, verbatim rates/sizes/refs — the numbers Tom tuned. */
export const REHAB_ITEMS: RehabItem[] = [
  { n: "Interior Paint", kind: "sqft", rate: 1.5, base: "house", ref: "$1.5/sqft", on: false },
  { n: "Exterior Paint", kind: "sqft", rate: 1.5, base: "building", ref: "$1.5/sqft", on: false },
  { n: "Light Fixtures and Blinds", kind: "flat", amt: 2000, ref: "$1,500-$2,500", on: false },
  { n: "Landscaping", kind: "flat", amt: 1000, ref: "$500-$1,500", on: false },
  { n: "Cleaning", kind: "flat", amt: 350, ref: "$350", on: false },
  { n: "Miscellaneous (faucet, drywall, toilet, doors)", kind: "flat", amt: 2000, ref: "$1,000-$3,000", on: false },
  { n: "Other Items (Custom)", kind: "flat", amt: 0, ref: "Custom", on: false },
  { n: "Kitchen Remodel", kind: "size", sizes: { Small: 6000, Medium: 8000, Large: 12000 }, ref: "S $6K / M $8K / L $12K", on: false },
  { n: "Bathroom Remodel", kind: "size", sizes: { Small: 3500, Medium: 5000, Large: 6500 }, ref: "S $3.5K / M $5K / L $6.5K", on: false },
  { n: "Bathroom Vanity", kind: "qty", amt: 800, ref: "$500-$2,000", on: false },
  { n: "Appliances", kind: "flat", amt: 3500, ref: "$3,500", on: false },
  { n: "Flooring", kind: "sqft", rate: 5, base: "house", ref: "$5/sqft", on: false },
  { n: "Roof", kind: "sqft", rate: 7, base: "building", ref: "$7/sqft", on: false },
  { n: "Windows", kind: "size", sizes: { Small: 300, Medium: 500, Large: 800 }, ref: "S $300 / M $500 / L $800", on: false },
  { n: "Electrical Panel", kind: "flat", amt: 1600, ref: "$1,600", on: false },
  { n: "New Plumbing", kind: "flat", amt: 6000, ref: "$6,000", on: false },
  { n: "Hot Water Heater", kind: "flat", amt: 1000, ref: "$800-$1,200", on: false },
  { n: "AC", kind: "flat", amt: 6000, ref: "$5,500-$7,000", on: false },
  { n: "Septic Tank and Drain Field", kind: "flat", amt: 8000, ref: "$5,000-$12,000", on: false },
  { n: "Pool Resurfacing", kind: "flat", amt: 10000, ref: "$5,000-$12,000", on: false },
  { n: "Sliding Door", kind: "flat", amt: 1200, ref: "$1,200", on: false },
  { n: "Drywall Repairs (per spot)", kind: "flat", amt: 5000, ref: "$5,000-$12,000", on: false },
  { n: "Replace Drywall (per wall)", kind: "qty", amt: 150, ref: "~$150/sect", on: false },
  { n: "Demo Wall", kind: "qty", amt: 500, ref: "~$500/wall", on: false },
];

const toNum = (v: unknown): number | null => {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string") {
    const n = parseFloat(v.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const isSize = (v: unknown): v is RehabSize => v === "Small" || v === "Medium" || v === "Large";

/** Is this row checked? Row override wins, else the catalog default. */
export function rowOn(item: RehabItem, row: RehabRow): boolean {
  return row.on ?? item.on;
}

/** One row's dollar price given the current sqft inputs. */
/** `seed` is the quantity to assume when the row has none of its own — pass
 * seedQty(item, lead). It MUST match what the stepper displays: the UI showed
 * `row.qty ?? seedQty(item, lead)` while this priced `row.qty ?? 1`, so on a
 * 3-bath house "Bathroom Remodel" read 3 and charged for 1 ($5,000 instead of
 * $15,000), and one click of "+" quadrupled the price. Defaults to 1 so callers
 * without a lead keep their old behaviour. */
export function rowPrice(item: RehabItem, row: RehabRow, house: number, bldg: number, seed = 1): number {
  switch (item.kind) {
    case "sqft": {
      const rate = row.rate ?? item.rate ?? 0;
      const sqft = item.base === "house" ? house : bldg;
      return rate * sqft;
    }
    case "size": {
      const size = row.size ?? "Medium";
      const base = item.sizes ? item.sizes[size] : 0;
      const qty = row.qty ?? seed;
      return base * qty;
    }
    case "qty": {
      const amt = row.amt ?? item.amt ?? 0;
      const qty = row.qty ?? seed;
      return amt * qty;
    }
    case "flat":
    default:
      return row.amt ?? item.amt ?? 0;
  }
}

/** Sum of every CHECKED row's price (v1 rehabCalc total).
 *
 * Pass `lead` wherever it is available: bath rows seed their quantity from the
 * bath count, and without it a 3-bath house is totalled as though every bath
 * row were a single bath. Optional so the signature stays backwards-compatible.
 */
export function rehabTotal(state: RehabCalcState, lead?: Lead): number {
  const house = state.house ?? 0;
  const bldg = state.bldg ?? 0;
  let total = 0;
  REHAB_ITEMS.forEach((item, k) => {
    const row = state.rows[k] ?? {};
    if (rowOn(item, row)) total += rowPrice(item, row, house, bldg, lead ? seedQty(item, lead) : 1);
  });
  return total;
}

/** Read a persisted rehabCalc blob (v1 or v2) into a typed state. Malformed
 * rows are dropped, never rendered — the narrow-door discipline. */
export function narrowRehabCalc(raw: unknown): RehabCalcState {
  const o = raw && typeof raw === "object" && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const rows: Record<number, RehabRow> = {};
  const rowsRaw = o["rows"];
  if (rowsRaw && typeof rowsRaw === "object" && !Array.isArray(rowsRaw)) {
    for (const [k, v] of Object.entries(rowsRaw as Record<string, unknown>)) {
      const idx = Number(k);
      if (!Number.isInteger(idx) || idx < 0 || idx >= REHAB_ITEMS.length) continue;
      if (!v || typeof v !== "object" || Array.isArray(v)) continue;
      const r = v as Record<string, unknown>;
      const row: RehabRow = {};
      if ("on" in r) row.on = r["on"] === 1 || r["on"] === true || r["on"] === "1";
      const rate = toNum(r["rate"]);
      if (rate !== null) row.rate = rate;
      const qty = toNum(r["qty"]);
      if (qty !== null) row.qty = qty;
      const amt = toNum(r["amt"]);
      if (amt !== null) row.amt = amt;
      if (isSize(r["size"])) row.size = r["size"];
      rows[idx] = row;
    }
  }
  return { house: toNum(o["house"]), bldg: toNum(o["bldg"]), rows };
}

/** v1 house sqft = living/heated (lead sqft, else county living override). */
export function defaultHouse(lead: Lead, fields?: Record<string, unknown>): number | null {
  return toNum(lead.sqft) ?? toNum(fields?.["countySqft"]);
}

/** v1 building sqft = gross (county gross), falling back to living sqft. */
export function defaultBldg(lead: Lead, fields?: Record<string, unknown>): number | null {
  return (
    toNum(fields?.["totalSqft"]) ??
    toNum(fields?.["countyGrossArea"]) ??
    toNum(lead.sqft) ??
    toNum(fields?.["countySqft"])
  );
}

/** v1 rule: bath rows prefill the property's bath count ONLY when it's a whole
 * number (2.5 → 1, don't guess); everything else defaults to 1. */
export function defaultBathQty(lead: Lead): number {
  const b = lead.baths;
  return b != null && Number.isFinite(b) && b > 0 && b % 1 === 0 ? b : 1;
}

/** Bath rows seed their qty from the bath count; other qty/size rows seed 1. */
export function seedQty(item: RehabItem, lead: Lead): number {
  return /bath/i.test(item.n) ? defaultBathQty(lead) : 1;
}

export const usd = (n: number): string => moneyExact(n);

/** Persist the sheet + mirror the total into the canonical `rehab` edit (v1:
 * p.rehab = "$X"). Only a real total overwrites rehab — a $0 sheet never blanks
 * a manually-typed rehab number (v1 guard). Whole-object replace on rehabCalc. */
export function opSaveRehabCalc(address: string, state: RehabCalcState, total: number): RecOp {
  const d: Record<string, unknown> = {
    rehabCalc: { house: state.house, bldg: state.bldg, rows: state.rows, total },
  };
  if (total > 0) d["rehab"] = usd(total);
  return { s: "lead_fields", op: "patch", k: natKey(address), d, rep: ["rehabCalc"] };
}
